/* Pixel snapping for Buffy's players (owner close-up, 2026-09-29: a soft,
   blurry Buffy at 1691 x 1304).

   Inside the stage every Buffy transform is a whole number of CSS px, but the
   element's final position on the SCREEN can still be fractional: an odd
   viewport width centers the stage on a half pixel, text blocks above can
   have fractional heights, a section can place him at `right: 12%`. The GPU
   then resamples the pixel art between device pixels and his outlines go
   soft.

   Each registered target reports the viewport position of its own origin
   (without the compensation it already wears); the scheduler rounds that to
   the device-pixel grid (devicePixelRatio) and hands back the tiny
   compensating offset, always under one device pixel. All reads happen
   before all writes, once per animation frame, and only when something
   could have moved: resize, a layout change (ResizeObserver on the page),
   web fonts loading, scroll, and the end of any CSS transition or animation
   (section reveals). */

const targets = new Set();
let queued = false;
let wired = false;
let ro = null;

/* The offset (CSS px) that moves `v` (CSS px) onto the device-pixel grid. */
export function snapOffset(v, dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1) {
  const d = v * dpr;
  // + 1e-3: an exact half pixel always rounds the same way (layout can
  // report 16.4999 in one state and 16.5 in another at DPR 1.5)
  const off = (Math.round(d + 1e-3) - d) / dpr;
  return Math.abs(off) < 1e-4 ? 0 : off;
}

function flush() {
  queued = false;
  const dpr = window.devicePixelRatio || 1;
  // read everything first, then write (no layout thrash)
  const reads = [];
  targets.forEach((t) => {
    const at = t.origin();
    if (at) reads.push([t, snapOffset(at.x, dpr), snapOffset(at.y, dpr)]);
  });
  reads.forEach(([t, x, y]) => t.apply(x, y));
}

export function requestSnap() {
  if (queued || typeof window === 'undefined') return;
  queued = true;
  window.requestAnimationFrame(flush);
}

function wire() {
  if (wired || typeof window === 'undefined') return;
  wired = true;
  window.addEventListener('resize', requestSnap);
  window.addEventListener('scroll', requestSnap, { passive: true, capture: true });
  document.addEventListener('transitionend', requestSnap, true);
  document.addEventListener('animationend', requestSnap, true);
  if (typeof ResizeObserver !== 'undefined') {
    ro = new ResizeObserver(requestSnap);
    ro.observe(document.documentElement);
    if (document.body) ro.observe(document.body);
  }
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(requestSnap);
  // a devicePixelRatio change (browser zoom, moving to another screen)
  const watchDpr = () => {
    const mq = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
    const onChange = () => { requestSnap(); watchDpr(); };
    if (mq.addEventListener) mq.addEventListener('change', onChange, { once: true });
  };
  if (window.matchMedia) watchDpr();
}

/* Register a target: { origin() -> {x, y} | null, apply(dx, dy) }.
   Returns the unregister function. */
export function addSnapTarget(target) {
  wire();
  targets.add(target);
  requestSnap();
  return () => { targets.delete(target); };
}
